import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'


export const StandupPouchModel = forwardRef(({ id, viewWidth, viewHeight, animationSpeed, backgroundColor, pouchColor, finishType, tearNotch, tearNotchOffset, width, height, gusset, hangHole, hangHoleOffset, zipper, zipperOffset, roundedCorners, sealWidth, artwork, valve, valveOffset }, ref) => {

    const controlRef = useRef(null);
    const [design, setDesign] = useState();

    var scene;
    var camera;
    var renderer;
    var myTexture;
    var materialG, materialST, materialM;
    var materialChildren;
    var controls;
    var notchesCompleted = false;
    var holeCompleted = false;
    var zipperCompleted = false;
    var artworkCompleted = false;
    var sealCompleted = false;
    var roundedCompleted = false;
    var valveCompleted = false;
    var zipTex, zipTex2, valTex;
    var wearZipTex, wearZipTex2, wearValTex;
    var tempMainColor = Utils.hexToRgb(pouchColor);
    var mainColor = new THREE.Color(`rgb(${tempMainColor.r}, ${tempMainColor.g}, ${tempMainColor.b})`);
    var bgColor;

    const loader = new GLTFLoader();

    const artworkBase64 = undefined

    useImperativeHandle(ref, () => ({
        refreshDesign: (value) => updateArtwork(value)
    }));

    const updateArtwork = (value) => {
        matsAndTextures(value);
        loadPlowBottom();
    }


    useEffect(() => {
        buildSceneAndAnimate();
        matsAndTextures(null);
        loadPlowBottom();
    });

    const buildSceneAndAnimate = () => {
        //scene, camera, and renderer are always necessary.
        scene = new THREE.Scene();

        camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 1000);

        renderer = new THREE.WebGLRenderer();
        renderer.setSize(viewWidth, viewHeight); //setting size of renderer is necessary.
        renderer.setPixelRatio(window.devicePixelRatio);
        camera.aspect = viewWidth / viewHeight;
        camera.updateProjectionMatrix();

        renderer.outputEncoding = THREE.sRGBEncoding; // setting for working with gltf objects

        var element = document.getElementById(`standupPouchModel_canvas${id}`);
        element.innerHTML = "";
        element.appendChild(renderer.domElement);

        /* Format is 0xRRGGBB for colors. */
        var ambientLight = new THREE.AmbientLight(0x0c0c0c);
        scene.add(ambientLight);
        var delight = new THREE.DirectionalLight(0xffffff, 0.3);
        delight.position.set(0, 0, 5);
        scene.add(delight);
        var delightB = new THREE.DirectionalLight(0xffffff, 0.3);
        delightB.position.set(2, 0, 4);
        scene.add(delightB);
        var delightC = new THREE.DirectionalLight(0xffffff, 0.3);
        delightC.position.set(-3, 0, 4);
        scene.add(delightC);
        var delight2 = new THREE.DirectionalLight(0xffffff, 0.3);
        delight2.position.set(0, 0, -5);
        scene.add(delight2);
        var delight3 = new THREE.DirectionalLight(0xffffff, 0.3);
        delight3.position.set(2, 0, -5);
        scene.add(delight3);
        var delight4 = new THREE.DirectionalLight(0xffffff, 0.3);
        delight4.position.set(-3, 0, -5);
        scene.add(delight4);

        if (backgroundColor) {
            var color = Utils.hexToRgb(backgroundColor);
            bgColor = new THREE.Color(`rgb(${color.r}, ${color.g}, ${color.b})`);
            scene.background = bgColor;
            document.body.style.backgroundColor = "#" + backgroundColor;
        }
        else {
            scene.background = new THREE.Color('white');
            document.body.style.backgroundColor = "#FFFFFF";
        }

        camera.position.set(1, 4, 35);

        controls = new OrbitControls(camera, renderer.domElement);
        animate();
    }


    const matsAndTextures = (artworkBase64) => {

        myTexture = new THREE.Texture();
        myTexture.flipY = false;
        myTexture.matrixAutoUpdate = true;
        myTexture.wrapS = THREE.RepeatWrapping;
        myTexture.wrapT = THREE.ClampToEdgeWrapping;

        zipTex = new THREE.Texture();
        zipTex.flipY = false;
        zipTex.matrixAutoUpdate = true;
        zipTex.wrapS = THREE.RepeatWrapping;
        zipTex.wrapT = THREE.RepeatWrapping;

        zipTex2 = new THREE.Texture();
        zipTex2.flipY = false;
        zipTex2.matrixAutoUpdate = true;
        zipTex2.wrapS = THREE.RepeatWrapping;
        zipTex2.wrapT = THREE.RepeatWrapping;

        valTex = new THREE.Texture();
        valTex.flipY = false;
        valTex.matrixAutoUpdate = true;
        valTex.wrapS = THREE.RepeatWrapping;
        valTex.wrapT = THREE.ClampToEdgeWrapping;

        if (artworkBase64) {
            //artwork means materials need to use the artwork textures
            let image = new Image();
            image.src = artworkBase64;
            myTexture.image = image;
            zipTex.image = image;
            zipTex2.image = image;
            valTex.image = image;
            image.onload = function () {
                myTexture.needsUpdate = true;
                zipTex.needsUpdate = true;
                zipTex2.needsUpdate = true;
                valTex.needsUpdate = true;
            }

            if (zipperCompleted) {
                let zoX = ((zipperOffset / 254) * 0.434) + 0.0039;
                zipTex.offset.set(zoX, 0.998);
                zipTex.needsUpdate = true;

                let z2X = 1 - (((zipperOffset / 254) * (1 - 0.5735)) + 0.0053);//256 //0.5735 //0.0053
                zipTex2.offset.set(z2X, 0);
                zipTex2.needsUpdate = true;

            }

            if (valveCompleted) {
                let voX = 1 - (((valveOffset / 248) * 0.43) + 0.008);
                valTex.offset.set(voX, 0.001);
                valTex.needsUpdate = true;
            }

            materialG = new THREE.MeshPhysicalMaterial();
            materialG.map = myTexture;
            materialG.clearcoat = 0.49;
            materialG.flatShading = false;

            materialST = new THREE.MeshPhongMaterial();
            materialST.map = myTexture;
            materialST.shininess = 36;
            materialST.flatShading = false;

            materialM = new THREE.MeshPhongMaterial();
            materialM.map = myTexture;
            materialM.shininess = 1;
            materialM.flatShading = false;

        } else {
            // !artworkCompleted means materials should be plain
            materialG = new THREE.MeshPhysicalMaterial(mainColor);
            materialG.clearcoat = 0.29;
            materialG.flatShading = false;

            materialST = new THREE.MeshPhongMaterial({ mainColor });
            materialST.shininess = 36;
            materialST.flatShading = false;

            materialM = new THREE.MeshPhongMaterial({ color: 0xff0000 });
            materialM.shininess = 1;
            materialM.flatShading = false;

        }

        var childColor = Utils.hexToRgb(backgroundColor);
        materialChildren = new THREE.MeshBasicMaterial({ color: 0xffffff });

    }

    const loadPlowBottom = () => {
        clearScene();
        var fileName = 'Blender/model.glb'; // Original same file as plowBottomW.json
        loader.load(fileName,
            function (gltf) {
                /* view controls */
                gltf.scene.children[0].position.set(0, 0, 0);
                controls.target = new THREE.Vector3(0, 0, 0);
                //hang hole positioning
                if (hangHole != 1 && hangHoleOffset) {
                    gltf.scene.children[0].children[2].position.y = 1 - (((hangHoleOffset / 1) + 3) * 0.00777681006);
                    gltf.scene.children[0].children[3].position.y = 1 - (((hangHoleOffset / 1) + 3) * 0.00777681006);
                }

                //notch positioning
                if (tearNotch != 1 || tearNotchOffset) {
                    gltf.scene.children[0].children[1].children[0].position.y = 1 - (((tearNotchOffset / 1) + 0.5) * 0.00777681006);
                    gltf.scene.children[0].children[0].children[0].position.y = 1 - (((tearNotchOffset / 1) + 0.5) * 0.00777681006);
                }

                //zipper positioning
                if (zipper != 1 && zipperOffset) {
                    gltf.scene.children[0].children[5].position.y = 1 - (((zipperOffset / 1) + 3) * 0.00777681006);
                    gltf.scene.children[0].children[6].position.y = 1 - (((zipperOffset / 1) + 3) * 0.00777681006);
                    //offsetting texture to look right as zipper moves up/down
                    if (artworkBase64) {
                        let zoX = ((zipperOffset / 254) * 0.434) + 0.0039;
                        zipTex.offset.set(zoX, 0.998);
                        zipTex.needsUpdate = true;

                        let z2X = 1 - (((zipperOffset / 254) * (1 - 0.5735)) + 0.0053);//256 //0.5735 //0.0053
                        zipTex2.offset.set(z2X, 0);
                        zipTex2.needsUpdate = true;
                    }
                }

                //valve positioning
                gltf.scene.children[0].children[4].position.x = -0.008;
                if (valve == 2 && valveOffset) {

                    gltf.scene.children[0].children[4].position.y = 1 - (((valveOffset / 1) + 9.5) * 0.00777681006);

                    // piecewise break 115 
                    if (valveOffset < 115) {
                        gltf.scene.children[0].children[4].rotation.x = -2.9872604848891653;
                        gltf.scene.children[0].children[4].rotation.y = 0;
                        gltf.scene.children[0].children[4].rotation.z = 3.1415926368424016;
                        gltf.scene.children[0].children[4].position.z = 0 + ((gltf.scene.children[0].children[4].position.y - 0.66) * 0.76);
                    } else if (valveOffset >= 115) {
                        gltf.scene.children[0].children[4].position.z = 0 + ((-0.62043604241 * 0.76) - ((valveOffset - 114) * 0.0022));
                        gltf.scene.children[0].children[4].rotation.x = -3.26;
                    }

                    if (artworkBase64) {
                        let voX = 1 - (((valveOffset / 248) * 0.43) + 0.008);
                        valTex.offset.set(voX, 0.001);
                        valTex.needsUpdate = true;
                    }
                }

                //seal width
                if (sealWidth == 1) {
                    gltf.scene.children[0].children[0].position.x = 1.0498287677764893 - ((10 - (5 / 1)) * 0.00777681006);
                    gltf.scene.children[0].children[1].position.x = -1.0827476978302002 + ((10 - (5 / 1)) * 0.00777681006);
                } else if (sealWidth == 2) {
                    gltf.scene.children[0].children[0].position.x = 1.0498287677764893 - ((10 - (8 / 1)) * 0.00777681006);
                    gltf.scene.children[0].children[1].position.x = -1.0827476978302002 + ((10 - (8 / 1)) * 0.00777681006);
                } else if (sealWidth == 3) {
                    gltf.scene.children[0].children[0].position.x = 1.0498287677764893 - ((10 - (10 / 1)) * 0.00777681006);
                    gltf.scene.children[0].children[1].position.x = -1.0827476978302002 + ((10 - (10 / 1)) * 0.00777681006);
                } else {
                    console.log("invalid seal width argument");
                }

                /* applying main pouch scaling */
                let widthScale = ((width / 1) / 25.4);
                let gScale = ((gusset / 1) / 25.4);
                let hScale = ((height / 1) / 25.4);
                gltf.scene.children[0].scale.set(widthScale, hScale, gScale);
                /* reversing scale for children so they retain original shape using 1/scale */
                let keepZ = gltf.scene.children[0].children[4].scale.z; //the gusset dimension being changed is unnecessary and will throw off the valve positioning function I created above
                gltf.scene.children[0].children[0].children[0].scale.set((-1.69710526315789473 / widthScale), (0.10091703056768559 / hScale), (0.33421052631578946 / gScale)); //notches
                gltf.scene.children[0].children[1].children[0].scale.set((1.03710526315789473 / widthScale), (0.10091703056768559 / hScale), (0.33421052631578946 / gScale));
                gltf.scene.children[0].children[3].scale.set((0.16710526315789473 / widthScale), (0.03021052631578946 / gScale), (0.17091703056768559 / hScale)); //sombrero
                gltf.scene.children[0].children[2].scale.set((0.16710526315789473 / widthScale), (0.03021052631578946 / gScale), (0.17091703056768559 / hScale)); //round
                gltf.scene.children[0].children[4].scale.set((1.76091 / widthScale), (1.89686 / hScale), keepZ); //valve

                /* applying children material */
                for (var i = 0; i < gltf.scene.children[0].children.length; i++) {
                    gltf.scene.children[0].children[i].material = materialChildren;
                    if (gltf.scene.children[0].children[i].children.length > 0) { //some children have children
                        for (var j = 0; j < gltf.scene.children[0].children[i].children.length; j++) {
                            gltf.scene.children[0].children[i].children[j].material = materialChildren;
                        }
                    }
                }//make sure to overwrite zipper and valve material below

                /* notches toggle  */
                if (tearNotchOffset) {
                    if (tearNotch.id == 1) {
                        gltf.scene.children[0].children[1].children[0].visible = false;
                        gltf.scene.children[0].children[0].children[0].visible = false;
                    } else {
                        gltf.scene.children[0].children[1].children[0].visible = true;
                        gltf.scene.children[0].children[0].children[0].visible = true;
                    }
                } else {
                    gltf.scene.children[0].children[1].children[0].visible = false;
                    gltf.scene.children[0].children[0].children[0].visible = false;
                }

                /* hang hole toggle */ //2,8,9 are sombrero //8: 25mmS  9: 20mmS  2: 30mmS
                if (hangHoleOffset) {
                    if (hangHole.id == 1) { //none
                        gltf.scene.children[0].children[3].visible = false;
                        gltf.scene.children[0].children[2].visible = false;
                    } else if (hangHole == 2 || hangHole == 8 || hangHole == 9) {//sombrero
                        gltf.scene.children[0].children[3].visible = true;
                        gltf.scene.children[0].children[2].visible = false;
                    } else {//circle
                        gltf.scene.children[0].children[3].visible = false;
                        gltf.scene.children[0].children[2].visible = true;
                    }
                } else {//no hang hole allowed
                    gltf.scene.children[0].children[3].visible = false;
                    gltf.scene.children[0].children[2].visible = false;
                }

                /* zipper toggle */ // 2-5 = zippers
                if (zipperOffset) {
                    if (zipper == 1) {
                        gltf.scene.children[0].children[5].visible = false;
                        gltf.scene.children[0].children[6].visible = false;
                    } else {
                        gltf.scene.children[0].children[5].visible = true;
                        gltf.scene.children[0].children[6].visible = true;
                    }
                } else {//zipper not allowed
                    gltf.scene.children[0].children[5].visible = false;
                    gltf.scene.children[0].children[6].visible = false;
                }

                /* valve toggle */
                if (valve && valveOffset) {
                    if (valve == 1) {
                        gltf.scene.children[0].children[4].visible = false;
                    } else {
                        gltf.scene.children[0].children[4].visible = true;
                    }
                } else { //valve not allowed
                    gltf.scene.children[0].children[4].visible = false;
                }

                /* finish type system */
                if (finishType == 1) { //gloss 
                    gltf.scene.children[0].material = materialG;

                    if (artworkBase64) {
                        wearValTex = new THREE.MeshPhysicalMaterial();
                        wearValTex.map = valTex;
                        wearZipTex = new THREE.MeshPhysicalMaterial(); //re-initializing zipper texture to match finish
                        wearZipTex.map = zipTex;
                        wearZipTex2 = new THREE.MeshPhysicalMaterial();
                        wearZipTex2.map = zipTex2;
                    } else {
                        wearValTex = new THREE.MeshPhysicalMaterial(mainColor);
                        wearZipTex = new THREE.MeshPhysicalMaterial({ mainColor });
                        wearZipTex2 = new THREE.MeshPhysicalMaterial({ mainColor });
                    }
                    wearValTex.clearcoat = 0.49;
                    wearValTex.flatShading = false;
                    wearZipTex.clearcoat = 0.49;
                    wearZipTex.flatShading = false;
                    wearZipTex2.clearcoat = 0.49;
                    wearZipTex2.flatShading = false;

                    gltf.scene.children[0].children[4].material = wearValTex;
                    gltf.scene.children[0].children[5].material = wearZipTex;
                    gltf.scene.children[0].children[6].material = wearZipTex2;

                } else if (finishType == 2) {//matte
                    gltf.scene.children[0].material = materialM;

                    if (artworkBase64) {
                        wearValTex = new THREE.MeshPhongMaterial();
                        wearValTex.map = valTex;
                        wearZipTex = new THREE.MeshPhongMaterial();
                        wearZipTex.map = zipTex;
                        wearZipTex2 = new THREE.MeshPhongMaterial();
                        wearZipTex2.map = zipTex2;
                    } else {
                        wearZipTex = new THREE.MeshPhongMaterial(mainColor);
                        wearZipTex2 = new THREE.MeshPhongMaterial(mainColor);
                        wearValTex = new THREE.MeshPhongMaterial(mainColor);
                    }
                    wearZipTex.shininess = 1;
                    wearZipTex.flatShading = false;
                    wearZipTex2.shininess = 1;
                    wearZipTex2.flatShading = false;
                    wearValTex.shininess = 1;
                    wearValTex.flatShading = false;

                    gltf.scene.children[0].children[5].material = wearZipTex; //zipper
                    gltf.scene.children[0].children[6].material = wearZipTex2; //backzipper
                    gltf.scene.children[0].children[4].material = wearValTex; //valve

                } else if (finishType == 3) {//soft touch matte
                    gltf.scene.children[0].material = materialST;

                    if (artworkBase64) {
                        wearValTex = new THREE.MeshPhongMaterial();
                        wearValTex.map = valTex;
                        wearZipTex = new THREE.MeshPhongMaterial();
                        wearZipTex.map = zipTex;
                        wearZipTex2 = new THREE.MeshPhongMaterial();
                        wearZipTex2.map = zipTex2;
                    } else {
                        wearValTex = new THREE.MeshPhongMaterial(mainColor);
                        wearZipTex = new THREE.MeshPhongMaterial(mainColor);
                        wearZipTex2 = new THREE.MeshPhongMaterial(mainColor);
                    }
                    wearValTex.shininess = 32;
                    wearValTex.flatShading = false;
                    wearZipTex.shininess = 36;
                    wearZipTex.flatShading = false;
                    wearZipTex2.shininess = 36;
                    wearZipTex2.flatShading = false;

                    gltf.scene.children[0].children[4].material = wearValTex; //valve
                    gltf.scene.children[0].children[5].material = wearZipTex; //zipper
                    gltf.scene.children[0].children[6].material = wearZipTex2; //backzipper
                }

                if (roundedCorners == 1) {
                    gltf.scene.children[0].children[1].children[1].visible = false;
                    gltf.scene.children[0].children[1].children[2].visible = false;
                    gltf.scene.children[0].children[0].children[1].visible = false;
                    gltf.scene.children[0].children[0].children[2].visible = false;
                } else {
                    gltf.scene.children[0].children[1].children[1].visible = true;
                    gltf.scene.children[0].children[1].children[2].visible = true;
                    gltf.scene.children[0].children[0].children[1].visible = true;
                    gltf.scene.children[0].children[0].children[2].visible = true;
                }
                console.log("id: ", id, "view width: ", viewWidth, "view height: ", viewHeight, "animation speed: ", animationSpeed, "background color: ", backgroundColor, "pouch color: ", pouchColor, "finish type: ", finishType, "tear notch: ", tearNotch, "tear notch offset: ", tearNotchOffset, "width/height/gusset: ", width, height, gusset, "hang hole: ", hangHole, "hang hole offset: ", hangHoleOffset, "zipper and offset: ", zipper, zipperOffset, "rounded corners: ", roundedCorners, "seal width: ", sealWidth, "artwork: ", artwork, "valve and offset: ", valve, valveOffset)
                scene.add(gltf.scene);
                if (!artworkBase64) {//valve and zipper didn't have expected materials after above segment ^^
                    //gltf.scene.children[0].material.color = mainColor;
                    gltf.scene.children[0].children[5].material = wearZipTex;
                    gltf.scene.children[0].children[5].material.color = mainColor;
                    gltf.scene.children[0].children[6].material = wearZipTex2;
                    gltf.scene.children[0].children[6].material.color = mainColor;
                    gltf.scene.children[0].children[4].material = wearValTex;
                    gltf.scene.children[0].children[4].material.color = mainColor;
                }

                materialChildren.color = bgColor;

                //console.log(artwork);
            }, undefined, function (error) {
                console.error("load error");
            });
    }

    const animate = () => {

        if (animationSpeed > 0) {
            requestAnimationFrame(animate);
            scene.rotation.y += animationSpeed;
            renderer.render(scene, camera);
        }
    }

    const clearScene = () => { //note: if you type the next digit before the current digit renders (in text boxes), you will likely see the bug described below
        for (var q = 0; q < scene.children.length; q++) {
            if (scene.children[q].isGroup) {
                scene.remove(scene.children[q]);
            }
        }
        for (var q = 0; q < scene.children.length; q++) { //repeated because the single previous rendering instance persisted when changing an offset value
            if (scene.children[q].isGroup) {
                scene.remove(scene.children[q]);
            }
        }
    }

    return (
        <div id={`standupPouchModel_canvas${id}`} className='standupPouchModel' ref={controlRef}>
        </div>
    )
});

export class Utils {

    static hexToRgb(hex) {
        var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    }
}